/**
 * PressPage — keenez Newsroom.
 *
 * A polished public newsroom matching the v2 landing redesign quality.
 * Keeps the shared LegalShell chrome (SiteHeader + SiteFooter + back-home + hero
 * band) and builds a rich body from the v2 landing primitives:
 *
 *   Hero (from LegalShell)
 *     1. Company at a glance — animated fast-facts band (CountUp where tasteful)
 *     2. Press kit / brand assets — downloadable-looking cards + color tokens
 *     3. Coverage / milestones — dated keenez announcement timeline
 *     4. Media contact CTA → press@keenez.com
 *
 * Conventions honored (CONVENTIONS ARE LAW): single indigo accent, token
 * surfaces (bg-card / border-border / glass / shadow-soft …), logical props,
 * shared variants + reduced-motion gating via v2 primitives, all new copy via
 * t('press.*', { defaultValue }). Existing press.* keys remain wired.
 *
 * Honest tone: milestones are framed as keenez announcements, not fabricated
 * third-party journalism. No invented publications or quotes.
 */
import { useTranslation } from 'react-i18next';
import {
  Download,
  Mail,
  ArrowUpRight,
  Building2,
  Globe2,
  Languages,
  Cpu,
  CalendarDays,
  Palette,
  Type,
  User,
  Sparkles,
  Rocket,
  ShieldCheck,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import { LegalShell } from '@/components/public/LegalShell';
import {
  Reveal,
  Stagger,
  CountUp,
  GlassCard,
  Eyebrow,
} from '@/components/landing/v2';
import { fadeInUp } from '@/components/landing/v2/animations';

// ─────────────────────────────────────────────────────────────────────────────
// Data — true to the product. Numeric stats drive tasteful count-ups.
// ─────────────────────────────────────────────────────────────────────────────

interface Fact {
  key: string;
  Icon: LucideIcon;
  /** Numeric value for CountUp, or null for a text fact. */
  to: number | null;
  suffix?: string;
  /** Static value shown when `to` is null. */
  value: string;
  label: string;
}

const FACTS: Fact[] = [
  { key: 'founded', Icon: CalendarDays, to: 2026, value: '2026', label: 'Founded' },
  { key: 'hq', Icon: Building2, to: null, value: 'Tokyo', label: 'Headquarters' },
  { key: 'engines', Icon: Globe2, to: 4, value: '4', label: 'Live payroll engines · JP / US / UK / DE' },
  { key: 'locales', Icon: Languages, to: 14, value: '14', label: 'Product locales' },
  { key: 'ai', Icon: Cpu, to: null, value: 'AI-native', label: 'Compliance copilot built in' },
];

interface BrandAsset {
  key: string;
  Icon: LucideIcon;
  name: string;
  meta: string;
  href: string;
  /** Real download vs. on-request placeholder. */
  ready: boolean;
}

const BRAND_ASSETS: BrandAsset[] = [
  { key: 'logo', Icon: Download, name: 'Logo · PNG', meta: 'Transparent · 1024px', href: '/brand/keenez-logo.png', ready: true },
  { key: 'logo2x', Icon: Download, name: 'Logo · PNG (2x)', meta: 'Transparent · 2048px', href: '/brand/keenez-logo@2x.png', ready: true },
  { key: 'wordmark', Icon: Type, name: 'Wordmark · SVG', meta: 'Vector · on request', href: 'mailto:press@keenez.com?subject=keenez%20wordmark%20asset', ready: false },
];

interface ColorToken {
  key: string;
  name: string;
  hex: string;
  /** Tailwind class for the swatch surface. */
  swatch: string;
  note: string;
}

const COLOR_TOKENS: ColorToken[] = [
  { key: 'indigo', name: 'Indigo', hex: '#1A1AFF', swatch: 'bg-[#1A1AFF]', note: 'Primary accent' },
  { key: 'ink', name: 'Ink', hex: '#0B0B14', swatch: 'bg-[#0B0B14]', note: 'Foreground' },
  { key: 'mist', name: 'Mist', hex: '#F4F4F8', swatch: 'bg-[#F4F4F8] border border-border', note: 'Surface' },
];

interface Milestone {
  key: string;
  Icon: LucideIcon;
  date: string;
  tag: string;
  title: string;
  body: string;
}

// Dated keenez announcements / milestones — framed as company news, NOT as
// third-party journalism. Honest, no fabricated publications or quotes.
const MILESTONES: Milestone[] = [
  {
    key: 'copilot',
    Icon: Sparkles,
    date: '2026-05',
    tag: 'Product',
    title: 'AI compliance copilot enters general availability',
    body: 'Border Buddy ships GA — a cross-border HR, tax, and social-security copilot that cites the source article and country pair on every answer.',
  },
  {
    key: 'locales',
    Icon: Languages,
    date: '2026-04',
    tag: 'Localization',
    title: 'Product reaches 14 locales at parity',
    body: 'The full keenez UI — including RTL layouts for Arabic and Urdu — reaches feature parity across 14 languages for globally distributed teams.',
  },
  {
    key: 'de',
    Icon: Globe2,
    date: '2026-03',
    tag: 'Coverage',
    title: 'Germany payroll engine goes live',
    body: 'Germany joins Japan, the United States, and the United Kingdom — bringing the live payroll engine count to four jurisdictions in one product.',
  },
  {
    key: 'ga',
    Icon: Rocket,
    date: '2026-02',
    tag: 'Milestone',
    title: 'keenez launches out of Tokyo',
    body: 'keenez opens to its first cross-border customers: Japan-native and global payroll, run from a single HR cloud instead of spreadsheets and bilingual contractors.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

export function PressPage() {
  const { t } = useTranslation();

  const toc = [
    { id: 'glance', label: t('press.glance.title', { defaultValue: 'Company at a glance' }) },
    { id: 'brand-assets', label: t('press.brand.title', { defaultValue: 'Brand assets' }) },
    { id: 'coverage', label: t('press.coverage.title', { defaultValue: 'Milestones' }) },
    { id: 'contact', label: t('press.contact.title', { defaultValue: 'Press contact' }) },
  ];

  return (
    <LegalShell
      eyebrow={t('press.eyebrow', { defaultValue: 'Newsroom' })}
      title={t('press.newsroomTitle', { defaultValue: 'keenez Newsroom' })}
      subtitle={t('press.newsroomSubtitle', {
        defaultValue:
          'keenez builds cross-border HR and payroll for Japan-rooted global teams — Japan-native and global payroll, run from a single AI-native HR cloud. Boilerplate, brand assets, and milestones below.',
      })}
      toc={toc}
    >
      {/* Boilerplate — kept from existing keys, leads the article. */}
      <section className="scroll-mt-24">
        <Reveal as="p" variants={fadeInUp} className="!mt-0 text-pretty">
          {t('press.boilerplate.p1')}
        </Reveal>
      </section>

      {/* ── 1 · Company at a glance ─────────────────────────────────────── */}
      <section id="glance" className="not-prose scroll-mt-24 mt-12">
        <Reveal variants={fadeInUp}>
          <Eyebrow>{t('press.glance.eyebrow', { defaultValue: 'Fast facts' })}</Eyebrow>
          <h2 className="display serif-jp mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-balance">
            {t('press.glance.title', { defaultValue: 'Company at a glance' })}
          </h2>
        </Reveal>

        <Stagger
          as="ul"
          className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
        >
          {FACTS.map((f) => (
            <Reveal
              key={f.key}
              as="li"
              variants={fadeInUp}
              className="group flex h-full flex-col rounded-2xl border border-border bg-card/80 p-4 shadow-soft backdrop-blur-sm transition-shadow duration-200 hover:shadow-card-lift"
            >
              <span
                aria-hidden
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-accent-50 text-accent-600 ring-1 ring-accent-100 dark:bg-accent-500/10 dark:text-accent-300 dark:ring-accent-500/20"
              >
                <f.Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="mt-3 num text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                {f.to != null ? (
                  <CountUp to={f.to} suffix={f.suffix} />
                ) : (
                  t(`press.glance.facts.${f.key}.value`, { defaultValue: f.value })
                )}
              </span>
              <span className="mt-1 text-xs leading-snug text-muted-foreground">
                {t(`press.glance.facts.${f.key}.label`, { defaultValue: f.label })}
              </span>
            </Reveal>
          ))}
        </Stagger>
      </section>

      {/* ── 2 · Press kit / brand assets ────────────────────────────────── */}
      <section id="brand-assets" className="not-prose scroll-mt-24 mt-14">
        <Reveal variants={fadeInUp}>
          <Eyebrow>{t('press.brand.eyebrow', { defaultValue: 'Press kit' })}</Eyebrow>
          <h2 className="display serif-jp mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-balance">
            {t('press.brand.title', { defaultValue: 'Brand assets' })}
          </h2>
          <p className="mt-3 max-w-2xl text-pretty text-sm leading-relaxed text-muted-foreground md:text-base">
            {t('press.brand.p1')}
          </p>
        </Reveal>

        {/* Downloadable-looking asset cards */}
        <Stagger className="mt-6 grid gap-3 sm:grid-cols-3">
          {BRAND_ASSETS.map((a) => (
            <Reveal key={a.key} variants={fadeInUp}>
              <GlassCard className="p-0" hover>
                <a href={a.href} className="block p-4">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-accent-50 text-accent-600 ring-1 ring-accent-100 dark:bg-accent-500/10 dark:text-accent-300 dark:ring-accent-500/20">
                      <a.Icon className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-foreground">
                    {t(`press.brand.assets.${a.key}.name`, { defaultValue: a.name })}
                  </p>
                  <p className="num mt-0.5 text-xs text-muted-foreground">
                    {t(`press.brand.assets.${a.key}.meta`, { defaultValue: a.meta })}
                  </p>
                  {!a.ready && (
                    <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground/80">
                      <Mail className="h-3 w-3" />
                      {t('press.brand.onRequest', { defaultValue: 'On request via press@keenez.com' })}
                    </p>
                  )}
                </a>
              </GlassCard>
            </Reveal>
          ))}
        </Stagger>

        {/* Color tokens */}
        <Reveal variants={fadeInUp} delay={0.05} className="mt-6">
          <div className="rounded-2xl border border-border bg-muted/30 p-5">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <Palette className="h-3.5 w-3.5" />
              {t('press.brand.colors', { defaultValue: 'Brand colors' })}
            </p>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {COLOR_TOKENS.map((c) => (
                <li key={c.key} className="flex items-center gap-3">
                  <span
                    aria-hidden
                    className={`h-10 w-10 shrink-0 rounded-xl shadow-soft ${c.swatch}`}
                  />
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground">
                      {t(`press.brand.tokens.${c.key}`, { defaultValue: c.name })}
                    </span>
                    <span className="num text-xs uppercase tracking-wider text-muted-foreground">
                      {c.hex}
                    </span>
                    <span className="text-[11px] text-muted-foreground/80">
                      {t(`press.brand.tokenNote.${c.key}`, { defaultValue: c.note })}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* Founder bio placeholder */}
        <Reveal variants={fadeInUp} delay={0.1} className="mt-4">
          <div className="flex items-start gap-4 rounded-2xl border border-border bg-card/80 p-5 shadow-soft">
            <span
              aria-hidden
              className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white"
            >
              <User className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t('press.brand.founder.title', { defaultValue: 'Founder bio' })}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {t('press.brand.founder.body', {
                  defaultValue:
                    'Founder headshot and bio available on request. Email press@keenez.com for high-resolution portraits and an approved short bio.',
                })}
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── 3 · Coverage / milestones timeline ──────────────────────────── */}
      <section id="coverage" className="not-prose scroll-mt-24 mt-14">
        <Reveal variants={fadeInUp}>
          <Eyebrow>{t('press.coverage.eyebrow', { defaultValue: 'Announcements' })}</Eyebrow>
          <h2 className="display serif-jp mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-balance">
            {t('press.coverage.title', { defaultValue: 'Milestones' })}
          </h2>
          <p className="mt-3 max-w-2xl text-pretty text-sm leading-relaxed text-muted-foreground md:text-base">
            {t('press.coverage.lead', {
              defaultValue:
                'keenez product and company milestones. These are our own announcements — for interviews or fact-checks, reach the press desk below.',
            })}
          </p>
        </Reveal>

        <Stagger as="ul" className="relative mt-8 space-y-4">
          {/* Timeline spine */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-2 start-[19px] hidden w-px bg-border sm:block"
          />
          {MILESTONES.map((m) => (
            <Reveal key={m.key} as="li" variants={fadeInUp}>
              <div className="group relative flex gap-4 rounded-2xl border border-border bg-card/80 p-5 shadow-soft backdrop-blur-sm transition-shadow duration-200 hover:shadow-card-lift">
                <span
                  aria-hidden
                  className="z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-50 text-accent-600 ring-1 ring-accent-100 dark:bg-accent-500/10 dark:text-accent-300 dark:ring-accent-500/20"
                >
                  <m.Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <time className="num text-xs font-medium tracking-wide text-muted-foreground">
                      {m.date}
                    </time>
                    <span className="inline-flex items-center rounded-md border border-border/70 bg-muted/50 px-2 py-0.5 text-[11px] font-medium tracking-wide text-muted-foreground">
                      {t(`press.coverage.items.${m.key}.tag`, { defaultValue: m.tag })}
                    </span>
                  </div>
                  <h3 className="mt-1.5 text-base font-semibold leading-snug text-foreground">
                    {t(`press.coverage.items.${m.key}.title`, { defaultValue: m.title })}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground text-pretty">
                    {t(`press.coverage.items.${m.key}.body`, { defaultValue: m.body })}
                  </p>
                </div>
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-5 bottom-0 h-px origin-start scale-x-0 rounded-full bg-gradient-to-r from-accent-500/60 to-fuchsia-500/40 transition-transform duration-300 group-hover:scale-x-100"
                />
              </div>
            </Reveal>
          ))}
        </Stagger>

        <Reveal
          as="p"
          variants={fadeInUp}
          delay={0.1}
          className="mt-6 flex items-center gap-2 text-xs text-muted-foreground"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
          {t('press.coverage.honest', {
            defaultValue:
              'All entries above are keenez announcements. We do not attribute quotes to journalists we have not worked with.',
          })}
        </Reveal>
      </section>

      {/* ── 4 · Media contact CTA ───────────────────────────────────────── */}
      <section id="contact" className="not-prose scroll-mt-24 mt-14">
        <Reveal variants={fadeInUp}>
          <div className="relative isolate overflow-hidden rounded-3xl border border-border glass-strong p-7 shadow-card-lift md:p-10">
            <div aria-hidden className="pointer-events-none absolute inset-0">
              <div className="absolute inset-0 grid-bg opacity-40" />
            </div>
            <div className="relative">
              <Eyebrow>{t('press.contact.eyebrow', { defaultValue: 'Press desk' })}</Eyebrow>
              <h2 className="display serif-jp mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-balance">
                {t('press.contact.title', { defaultValue: 'Press contact' })}
              </h2>
              <p className="mt-3 max-w-xl text-pretty text-sm leading-relaxed text-muted-foreground md:text-base">
                {t('press.contact.p1')}{' '}
                <a href="mailto:press@keenez.com" className="font-medium text-primary hover:underline">
                  press@keenez.com
                </a>
                .
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="mailto:press@keenez.com"
                  className="gradient-primary inline-flex h-11 items-center gap-2 rounded-xl px-6 text-sm font-semibold text-white shadow-glow transition hover:shadow-glow-pink"
                >
                  <Mail className="h-4 w-4" />
                  {t('press.contact.cta', { defaultValue: 'Email the press desk' })}
                </a>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" aria-hidden />
                  {t('press.contact.hq', { defaultValue: 'Tokyo · Berlin' })}
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </LegalShell>
  );
}
